import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "semantic-ui-react";
import {
  GET_EMPLOYEES,
  SG_GET_ORGANIZATION_GROUP,
  SG_ADD_DEBRIEF_SCHEDULE,
  SG_GET_SURVEY_STRUCTURES,
  CLEAR_ORGANIZATION_GROUP,
  SG_FETCH_ORGANIZATION_GROUP,
  SG_EMPLOYEE_CATEGORY_SEARCH,
} from "constants/actions";
import { SURVEY_TYPE } from "constants/surveys";
import { useScheduleContext, ScheduleProvider } from "./ScheduleContext";
import ScheduleBreadcrumb from "./ScheduleBreadcrumb";

// Icons
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.7L16.2,16.2Z" />
  </svg>
);

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

// Timezone options
const timezones = [
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
  { value: "America/Anchorage", label: "Alaska Time (AK)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Halifax", label: "Atlantic Time (AT)" },
  { value: "UTC", label: "UTC/GMT" },
  { value: "Europe/London", label: "UK Time (GMT/BST)" },
  { value: "Europe/Paris", label: "Central European Time (CET)" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST)" },
  { value: "Australia/Sydney", label: "Australian Eastern Time (AET)" },
];

const surveyTypeLabels = {
  culture_audit: "Culture Audit",
  pulse_survey: "Pulse Survey",
  "360_review": "360 Review",
  edi_survey: "EDI Survey",
  custom: "Custom Survey",
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "Not set";
  try {
    const date = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  } catch {
    return dateStr;
  }
};

const formatTimeDisplay = (timeStr) => {
  if (!timeStr) return "Not set";
  const [hourStr, minuteStr] = (timeStr || "").split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return timeStr;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  const paddedMinute = String(minute).padStart(2, "0");
  return `${hour12}:${paddedMinute} ${period}`;
};

const resolveTimezoneLabel = (value) => {
  if (!value) return "Not set";
  const match = timezones.find((tz) => tz.value === value);
  return match?.label || value;
};

const ScheduleSurvey = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { scheduleSurveyWithGroup, scheduleFlow } = useScheduleContext();

  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1 = Survey Details, 2 = Select Recipients, 3 = Confirm

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    surveyType: "culture_audit",
    timezone: "America/New_York",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    createPublicLink: false,
    useEmployeeIds: false,
  });

  // Category selection state (from MultipleScheduler.js)
  const [selectedPrimary, setSelectedPrimary] = useState([]);
  const [selectedSecondary, setSelectedSecondary] = useState([]);
  const [appliedPrimary, setAppliedPrimary] = useState([]);
  const [appliedSecondary, setAppliedSecondary] = useState([]);

  // Grouping mode
  const [recipientMode, setRecipientMode] = useState("new"); // 'new' | 'existing'
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupName, setGroupName] = useState("");

  // Group search and pagination state
  const [groupSearchTerm, setGroupSearchTerm] = useState("");
  const [groupPage, setGroupPage] = useState(1);
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);

  // Survey scheduling state

  // Recipient selection state (adapted from GroupDetail)
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("individual"); // "individual" or "category"
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Bulk category selection state
  const [bulkCategoryId, setBulkCategoryId] = useState(null);
  const [bulkCategoryValueId, setBulkCategoryValueId] = useState(null);

  // Redux state
  const {
    employees,
    get_employee_org_category,
    get_survey_structure,
    pending,
    get_auth,
    organization_groups,
    groups_pagination,
    organization_group,
    group_pending,
    employeeCategorySearch,
  } = useSelector(
    (state) => ({
      employees: Array.isArray(state.employees?.employees)
        ? state.employees.employees
        : [],
      get_employee_org_category: Array.isArray(
        state.employee_category?.employee_org_category
      )
        ? state.employee_category.employee_org_category
        : [],
      get_survey_structure: Array.isArray(
        state.surveystructure?.survey_structure
      )
        ? state.surveystructure.survey_structure
        : [],
      pending: state.surveys?.pending || false,
      get_auth: state.auth || {},
      organization_groups: state.organization_group?.organization_groups,
      groups_pagination: state.organization_group?.page || {},
      organization_group: state.organization_group?.organization_group || {}, // The newly created/edited group
      group_pending: state.organization_group?.pending || false, // Group operation pending state
      employeeCategorySearch: state.employee_category?.search,
    }),
    shallowEqual
  );

  const MEMBERS_PAGE_SIZE = 10;

  const selectedGroupLoaded =
    selectedGroup && organization_group?.id === selectedGroup.id;

  const selectedGroupMembers = useMemo(() => {
    if (!selectedGroupLoaded) {
      return [];
    }

    return Array.isArray(organization_group?.members)
      ? organization_group.members
      : [];
  }, [organization_group?.members, selectedGroupLoaded]);

  const sortedGroupMembers = useMemo(() => {
    if (!selectedGroupLoaded) {
      return [];
    }

    const members = [...selectedGroupMembers];
    const getSortKey = (member) => {
      const last = member?.last_name || "";
      const first = member?.first_name || "";
      const nameKey = `${last} ${first}`.trim();
      const fallback = member?.email || "";
      return (nameKey || fallback).toLowerCase();
    };

    members.sort((a, b) => {
      const keyA = getSortKey(a);
      const keyB = getSortKey(b);
      if (keyA < keyB) return -1;
      if (keyA > keyB) return 1;
      return 0;
    });

    return members;
  }, [selectedGroupLoaded, selectedGroupMembers]);

  const [visibleGroupMembersCount, setVisibleGroupMembersCount] = useState(
    MEMBERS_PAGE_SIZE
  );

  useEffect(() => {
    setVisibleGroupMembersCount(MEMBERS_PAGE_SIZE);
  }, [recipientMode, selectedGroup?.id, selectedGroupLoaded]);

  useEffect(() => {
    if (!isConfirmModalOpen) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsConfirmModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isConfirmModalOpen]);

  const recipientSummaryCount =
    recipientMode === "existing"
      ? selectedGroupLoaded
        ? sortedGroupMembers.length
        : selectedGroup?.members_count || 0
      : selectedEmployees.length;

  const isExistingGroupView = recipientMode === "existing";

  const surveyTypeDisplay = useMemo(
    () => surveyTypeLabels[formData.surveyType] || "Custom Survey",
    [formData.surveyType]
  );

  const timezoneDisplayLabel = useMemo(
    () => resolveTimezoneLabel(formData.timezone),
    [formData.timezone]
  );

  const groupDisplayName = useMemo(() => {
    if (recipientMode === "existing") {
      return selectedGroup?.name?.trim() || "Existing Group";
    }

    if (groupName && groupName.trim()) {
      return groupName.trim();
    }

    return "New Survey Group";
  }, [recipientMode, selectedGroup?.name, groupName]);

  const publicAccessSummary = useMemo(() => {
    if (!formData.createPublicLink) return [];
    const summary = ["Public survey link enabled"];
    if (formData.useEmployeeIds) {
      summary.push("Requires employee ID to respond");
    }
    return summary;
  }, [formData.createPublicLink, formData.useEmployeeIds]);

  const scheduleWindowSummary = useMemo(() => {
    const dateText = formatDateDisplay(formData.startDate);
    const timeText = formatTimeDisplay(formData.startTime);

    if (!formData.startDate || !formData.startTime) {
      return dateText;
    }

    return `${dateText} at ${timeText}`;
  }, [formData.startDate, formData.startTime]);

  const displayedGroupMembers = sortedGroupMembers.slice(
    0,
    visibleGroupMembersCount
  );

  const hasMoreGroupMembers =
    sortedGroupMembers.length > visibleGroupMembersCount;

  const handleLoadMoreMembers = useCallback(() => {
    setVisibleGroupMembersCount((prev) =>
      Math.min(prev + MEMBERS_PAGE_SIZE, sortedGroupMembers.length)
    );
  }, [sortedGroupMembers.length]);
  // Fetch survey structure if missing and org id present
  useEffect(() => {
    const orgId = Number(get_auth?.organization_id);
    if (orgId && (!get_survey_structure || get_survey_structure.length === 0)) {
      dispatch({
        type: SG_GET_SURVEY_STRUCTURES,
        payload: `organization=${orgId}`,
      });
    }
  }, [dispatch, get_auth?.organization_id, get_survey_structure]);

  // Clear stale state
  useEffect(() => {
    dispatch({
      type: CLEAR_ORGANIZATION_GROUP
    })
  }, []);

  // Load required data on mount
  useEffect(() => {
    // Load employees and categories if needed
    if (!employees?.length) {
      dispatch({ type: GET_EMPLOYEES });
    }
    // Add other necessary data loading here
  }, [dispatch, employees?.length]);

  // Fetch groups once in existing mode (client-side search/sort only)
  useEffect(() => {
    if (recipientMode === "existing") {
      // Preload up to 500 groups once; filter/sort locally afterwards
      if (!Array.isArray(organization_groups) || organization_groups.length === 0) {
        dispatch({
          type: SG_GET_ORGANIZATION_GROUP,
          payload: { type: 'survey', page: 1, page_size: 500 },
        });
      }
    }
  }, [dispatch, recipientMode]);

  // Reset group selection when switching modes
  useEffect(() => {
    if (recipientMode === "new") {
      setSelectedGroup(null);
      setGroupSearchTerm("");
      setGroupPage(1);
      setIsGroupDropdownOpen(false);
      setActiveTab("individual");
      setSelectedCategories([]);
      setSelectedPrimary([]);
      setSelectedSecondary([]);
      setAppliedPrimary([]);
      setAppliedSecondary([]);
    } else if (recipientMode === "existing") {
      // Clear category selections when switching to existing mode
      setSelectedPrimary([]);
      setSelectedSecondary([]);
      setSelectedCategories([]);
      setAppliedPrimary([]);
      setAppliedSecondary([]);
      setActiveTab("category");
    }
  }, [recipientMode]);

  // Click-away functionality for group dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isGroupDropdownOpen &&
        !event.target.closest(".group-search-container")
      ) {
        setIsGroupDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isGroupDropdownOpen]);

  // Navigate automatically when the flow completes successfully
  useEffect(() => {
    if (scheduleFlow?.step === "done" && !scheduleFlow.active) {
      addToast("Survey scheduled successfully!", {
        appearance: "success",
        autoDismiss: true,
      });
      navigate("/leader/audit/schedule-v2");
    }
  }, [scheduleFlow?.step, scheduleFlow?.active, addToast, navigate]);

  // Build combined categories for the accordion (includes both employee filtering and survey scheduling)
  const accordionCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    const categories = [];

    // Add primary categories for survey scheduling (always first)
    const primaryCat = get_survey_structure[0].categories.find(
      (c) => c.priority === "primary"
    );
    if (primaryCat) {
      categories.push({
        id: `primary-${primaryCat.id}`,
        name: `${primaryCat.name} (Primary) *`,
        priority: "primary",
        type: "survey-scheduling",
        options: primaryCat.options.map((opt) => ({
          id: opt.id,
          name: opt.text || opt.name,
          text: opt.text || opt.name,
          name_id: opt.id,
          value: opt.name,
        })),
      });
    }

    // Add secondary categories for survey scheduling (if exists)
    const secondaryCat = get_survey_structure[0].categories.find(
      (c) => c.priority === "secondary"
    );
    if (secondaryCat) {
      categories.push({
        id: `secondary-${secondaryCat.id}`,
        name: `${secondaryCat.name} (Secondary)`,
        priority: "secondary",
        type: "survey-scheduling",
        options: secondaryCat.options.map((opt) => ({
          id: opt.id,
          name: opt.text || opt.name,
          text: opt.text || opt.name,
          name_id: opt.id,
          value: opt.name,
        })),
      });
    }

    // Add regular employee filtering categories
    const employeeFilteringCats = get_survey_structure[0].categories
      .filter(
        (cat) =>
          cat.visibility !== "private" &&
          !cat.demographic &&
          cat.priority !== "primary" &&
          cat.priority !== "secondary"
      )
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        type: "employee-filtering",
        options: cat.options.map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));

    return [...categories, ...employeeFilteringCats];
  }, [get_survey_structure]);

  // Build primary and secondary category options (from MultipleScheduler.js pattern)
  const primaryCategory = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    const primaryCat = get_survey_structure[0].categories.find(
      (c) => c.priority === "primary"
    );
    if (!primaryCat) return [];

    return primaryCat.options.map((opt) => ({
      value: opt.name,
      label: opt.text || opt.name,
      name: opt.text || opt.name,
      name_id: opt.id,
    }));
  }, [get_survey_structure]);

  const secondaryCategory = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    const secondaryCat = get_survey_structure[0].categories.find(
      (c) => c.priority === "secondary"
    );
    if (!secondaryCat) return [];

    return secondaryCat.options.map((opt) => ({
      value: opt.name,
      label: opt.text || opt.name,
      name: opt.text || opt.name,
      name_id: opt.id,
    }));
  }, [get_survey_structure]);

  // Build filterCategories for bulk dropdowns (similar to GroupDetail's AddEmployeesModal)
  const filterCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    return get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== "private" && !cat.demographic)
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        options: (cat.options || []).map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));
  }, [get_survey_structure]);

  // Derive the currently selected category + value
  const selectedBulkCategory = useMemo(
    () => filterCategories.find((cat) => cat.id === bulkCategoryId) || null,
    [filterCategories, bulkCategoryId]
  );

  const selectedBulkCategoryValue = useMemo(
    () =>
      selectedBulkCategory?.options?.find(
        (opt) => opt.id === bulkCategoryValueId
      ) || null,
    [selectedBulkCategory, bulkCategoryValueId]
  );

  // Sorted options for the Value dropdown (alphabetically by text)
  const sortedCategoryValueOptions = useMemo(() => {
    if (!selectedBulkCategory?.options) return [];
    return [...selectedBulkCategory.options]
      .sort((a, b) => {
        const textA = (a.text || a.name || '').toLowerCase();
        const textB = (b.text || b.name || '').toLowerCase();
        return textA.localeCompare(textB);
      })
      .map((opt) => ({
        key: opt.id,
        text: opt.text || opt.name || '',
        value: opt.id,
      }));
  }, [selectedBulkCategory]);

  // Derive bulk search loading + data from employeeCategorySearch
  const bulkLoading = !!employeeCategorySearch?.pending;
  const bulkMatchCount = employeeCategorySearch?.count || 0;
  const bulkResults = employeeCategorySearch?.results || [];

  // Gather selected category IDs used for filtering
  const activeCategoryFilters = useMemo(() => {
    return [
      ...selectedCategories,
      ...selectedPrimary.map((sp) => sp.name_id),
      ...selectedSecondary.map((ss) => ss.name_id),
    ];
  }, [selectedCategories, selectedPrimary, selectedSecondary]);

  // Filter employees based on search term and selected categories
  const filteredEmployees = useMemo(() => {
    if (!Array.isArray(employees)) return [];

    let filtered = employees.filter((emp) => emp.status === 1); // Only active employees

    if (activeCategoryFilters.length > 0) {
      filtered = filtered.filter((emp) => {
        const empCategories = get_employee_org_category.find(
          (empCat) => empCat.employee === emp.id
        );

        if (!empCategories?.category) return false;

        // Treat selected categories as OR; match when employee has any selection
        return activeCategoryFilters.some((selectedCatId) => {
          return empCategories.category.some(
            (cat) =>
              cat.value_id === selectedCatId || cat.value === selectedCatId
          );
        });
      });
    } else if (searchTerm) {
      // Only apply search filter if no categories are selected
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(lowerSearch) ||
          emp.last_name?.toLowerCase().includes(lowerSearch) ||
          emp.email?.toLowerCase().includes(lowerSearch)
      );
    }

    return filtered;
  }, [
    employees,
    searchTerm,
    activeCategoryFilters,
    get_employee_org_category,
  ]);

  // Employees matched by filters but not yet selected
  const addableEmployees = useMemo(
    () =>
      filteredEmployees.filter((emp) => !selectedEmployees.includes(emp.id)),
    [filteredEmployees, selectedEmployees]
  );

  // --- Date boundary helpers (prevent selecting past/invalid dates) ---
  // --- Timezone-aware helpers (compare wall-clock in selected TZ) ---
  const getNowInTZ = (tz) => {
    try {
      const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz || "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const parts = Object.fromEntries(fmt.formatToParts(new Date()).map(p => [p.type, p.value]));
      return {
        y: Number(parts.year),
        m: Number(parts.month),
        d: Number(parts.day),
        H: Number(parts.hour),
        M: Number(parts.minute),
      };
    } catch {
      const n = new Date();
      return { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate(), H: n.getHours(), M: n.getMinutes() };
    }
  };
  
  const parseYMD = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    return { y, m, d };
  };
  
  const parseHM = (s) => {
    if (!s) return null;
    const [H, M] = s.split(":").map(Number);
    return { H, M };
  };
  
  const combineDateTime = (ymd, hm) => {
    if (!ymd || !hm) return null;
    return { y: ymd.y, m: ymd.m, d: ymd.d, H: hm.H, M: hm.M };
  };
  
  const cmpDateTime = (a, b) => {
    if (!a || !b) return 0;
    if (a.y !== b.y) return a.y - b.y;
    if (a.m !== b.m) return a.m - b.m;
    if (a.d !== b.d) return a.d - b.d;
    if (a.H !== b.H) return a.H - b.H;
    return a.M - b.M;
  };
  
  const todayForSelectedTZ = useMemo(() => {
    try {
      const now = new Date();
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: formData.timezone || "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(now);
      const y = parts.find((p) => p.type === "year")?.value || "1970";
      const m = parts.find((p) => p.type === "month")?.value || "01";
      const d = parts.find((p) => p.type === "day")?.value || "01";
      return `${y}-${m}-${d}`; // YYYY-MM-DD
    } catch {
      // Fallback to local today if timezone formatting fails
      const pad = (n) => String(n).padStart(2, "0");
      const n = new Date();
      return `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}`;
    }
  }, [formData.timezone]);

  const startDateMin = null; // Allow past dates
  const endDateMin = useMemo(() => {
    if (formData.startDate) {
      return formData.startDate; // end date cannot be before selected start date
    }
    return null; // Allow past dates when no start date is selected
  }, [formData.startDate]);

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "startDate") {
        // If the existing endDate is before the new startDate, bump it to startDate
        if (next.endDate && next.endDate < value) {
          next.endDate = value;
        }
      }
      return next;
    });
  };

  // Handle employee selection
  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  // Handle category selection (for employee filtering)
  const handleCategoryToggle = (categoryOptionId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryOptionId)
        ? prev.filter((id) => id !== categoryOptionId)
        : [...prev, categoryOptionId]
    );
  };

  // Handle primary/secondary category selection for survey scheduling
  const handleSurveySchedulingCategoryToggle = (
    category,
    option,
    categoryType
  ) => {
    if (categoryType === "primary") {
      setSelectedPrimary((prev) => {
        const isSelected = prev.some((sp) => sp.name_id === option.name_id);
        if (isSelected) {
          return prev.filter((sp) => sp.name_id !== option.name_id);
        } else {
          return [
            ...prev,
            {
              value: option.value,
              label: option.name,
              name: option.name,
              name_id: option.name_id,
            },
          ];
        }
      });
    } else if (categoryType === "secondary") {
      setSelectedSecondary((prev) => {
        const isSelected = prev.some((ss) => ss.name_id === option.name_id);
        if (isSelected) {
          return prev.filter((ss) => ss.name_id !== option.name_id);
        } else {
          return [
            ...prev,
            {
              value: option.value,
              label: option.name,
              name: option.name,
              name_id: option.name_id,
            },
          ];
        }
      });
    }
  };

  // Toggle category expand
  const toggleCategoryExpand = (categoryId) => {
    setExpandedCategory((prev) => (prev === categoryId ? null : categoryId));
  };

  // Add employees from category selection
  const addCategoryEmployees = () => {
    const employeeIds = addableEmployees.map((emp) => emp.id);

    if (employeeIds.length === 0) {
      // Nothing new to add; just tidy up selections
      setSelectedCategories([]);
      setSelectedPrimary([]);
      setSelectedSecondary([]);
      setExpandedCategory(null);
      return;
    }

    setSelectedEmployees((prev) => [...new Set([...prev, ...employeeIds])]);

    // Persist selected primary/secondary categories for scheduling payload
    if (selectedPrimary.length > 0) {
      setAppliedPrimary((prev) => {
        const merged = new Map(prev.map((item) => [item.name_id, item]));
        selectedPrimary.forEach((item) => merged.set(item.name_id, item));
        return Array.from(merged.values());
      });
    }

    if (selectedSecondary.length > 0) {
      setAppliedSecondary((prev) => {
        const merged = new Map(prev.map((item) => [item.name_id, item]));
        selectedSecondary.forEach((item) => merged.set(item.name_id, item));
        return Array.from(merged.values());
      });
    }

    // Clear selections so checkboxes reset
    setSelectedCategories([]);
    setSelectedPrimary([]);
    setSelectedSecondary([]);
    setExpandedCategory(null);
  };

  // Clear all selected employees
  const clearAllEmployees = () => {
    setSelectedEmployees([]);
    setSelectedCategories([]);
    setSelectedPrimary([]);
    setSelectedSecondary([]);
    setAppliedPrimary([]);
    setAppliedSecondary([]);
    setExpandedCategory(null);
  };

  // Bulk category handlers
  const handleBulkCategoryPreview = () => {
    if (!selectedBulkCategory || !selectedBulkCategoryValue) {
      return;
    }
    dispatch({
      type: SG_EMPLOYEE_CATEGORY_SEARCH,
      payload: {
        page: 1,
        page_size: 10000, // Fetch all matching contacts (increased from 50)
        category_name_id: selectedBulkCategory.id,
        category_value_id: selectedBulkCategoryValue.id,
        category_priority: selectedBulkCategory.priority,
        ordering: "employee__first_name",
      },
    });
  };

  const handleBulkCategoryConfirm = () => {
    if (!bulkResults || bulkResults.length === 0) return;

    const newIds = bulkResults
      .map((row) => row.employee_id || row.employee || row.id)
      .filter(Boolean);

    if (!newIds.length) return;

    setSelectedEmployees((prev) => {
      const existing = new Set(prev);
      newIds.forEach((id) => existing.add(id));
      return Array.from(existing);
    });
  };

  const validateStepOne = () => {
    if (!formData.name || !formData.name.trim()) {
      addToast("Please enter a survey name", { appearance: "error" });
      return false;
    }

    if (!formData.startDate || !formData.startTime) {
      addToast("Please set a start date and time", { appearance: "error" });
      return false;
    }

    if (!formData.endDate || !formData.endTime) {
      addToast("Please set an end date and time", { appearance: "error" });
      return false;
    }

    const startDT = combineDateTime(
      parseYMD(formData.startDate),
      parseHM(formData.startTime)
    );
    const endDT = combineDateTime(
      parseYMD(formData.endDate),
      parseHM(formData.endTime)
    );

    if (!startDT || !endDT) {
      addToast("Please complete the start and end date/time", {
        appearance: "error",
      });
      return false;
    }

    if (cmpDateTime(endDT, startDT) <= 0) {
      addToast("End date and time must be after start date and time", {
        appearance: "error",
      });
      return false;
    }

    return true;
  };

  const validateRecipientsStep = () => {
    // If using public link, recipients are optional
    if (formData.createPublicLink) {
      return true;
    }

    if (recipientMode === "existing" && !selectedGroup) {
      addToast("Please select an existing group", { appearance: "error" });
      return false;
    }

    if (recipientMode === "new" && selectedEmployees.length === 0) {
      addToast("Please add at least one employee to the new group", {
        appearance: "error",
      });
      return false;
    }

    return true;
  };

  // Step navigation
  const goToNextStep = () => {
    if (currentStep === 1 && validateStepOne()) {
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2 && validateRecipientsStep()) {
      setCurrentStep(3);
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const goDirectToStep = (step) => {
    if (step < 1 || step > 3) return;
    if (step >= currentStep) return; // Prevent skipping forward without validation
    setCurrentStep(step);
  };

  const renderFlowStatus = () => {
    if (!scheduleFlow.active) return null;

    return (
      <FlowStatusSection>
        <FlowStatusHeader>
          <ClockIcon />
          <span>Processing Survey Schedule...</span>
        </FlowStatusHeader>
        <FlowSteps>
          <FlowStep completed={scheduleFlow.step !== "creatingGroup"}>
            <FlowStepIndicator active={scheduleFlow.step === "creatingGroup"} />
            <span>Creating group</span>
          </FlowStep>
          <FlowStepConnector />
          <FlowStep
            completed={
              scheduleFlow.step === "waitingSchedule" ||
              scheduleFlow.step === "done"
            }
          >
            <FlowStepIndicator
              active={scheduleFlow.step === "waitingBulk"}
            />
            <span>Adding contacts</span>
          </FlowStep>
          <FlowStepConnector />
          <FlowStep completed={scheduleFlow.step === "done"}>
            <FlowStepIndicator
              active={scheduleFlow.step === "waitingSchedule"}
            />
            <span>Scheduling survey</span>
          </FlowStep>
        </FlowSteps>
      </FlowStatusSection>
    );
  };

  // Helper to combine date & time into ISO string
  const toLocalDate = (date, time) => {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}`);
    return d.toISOString();
  };

  const mapTypeOf = (str) => {
    switch (str) {
      case "culture_audit":
        return SURVEY_TYPE?.AUDIT ?? SURVEY_TYPE?.AUDIT_ASSESSMENT ?? 1;
      case "pulse_survey":
        return SURVEY_TYPE?.PULSE ?? SURVEY_TYPE?.PULSE_SURVEY ?? 2;
      case "360_review":
        return SURVEY_TYPE?.REVIEW_360 ?? SURVEY_TYPE?.REVIEW360 ?? 3;
      case "edi_survey":
        return SURVEY_TYPE?.EDI ?? SURVEY_TYPE?.EDI_SURVEY ?? 4;
      case "custom":
      default:
        return SURVEY_TYPE?.AUDIT ?? 1;
    }
  };

  // Helper function to create schedule payload (category-based like MultipleScheduler.js)
  const createSchedulePayload = () => {
    const startIso = toLocalDate(formData.startDate, formData.startTime);
    const endIso = toLocalDate(formData.endDate, formData.endTime);

    const basePayload = {
      user_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      type_of: mapTypeOf(formData?.surveyType),
      scheduled_for: startIso,
      time_zone: formData.timezone,
      name: formData.name?.trim() || "",
      close_at: endIso,
      organization: Number(get_auth?.organization_id),
      survey_structure: get_survey_structure?.[0]?.id || null,
      include_members: !!formData.createPublicLink,
      require_id: !!formData.useEmployeeIds,
    };

    const primaryForPayload =
      appliedPrimary.length > 0 ? appliedPrimary : selectedPrimary;
    const secondaryForPayload =
      appliedSecondary.length > 0 ? appliedSecondary : selectedSecondary;

    // For new groups with category selection, use multiple_list structure
    if (recipientMode === "new" && primaryForPayload.length > 0) {
      return {
        ...basePayload,
        multiple_list: primaryForPayload.map((sp) => [sp.name, sp.name_id]),
        secondary:
          secondaryForPayload?.length > 0
            ? secondaryForPayload.map((ss) => ss.value)
            : null,
      };
    }

    // For existing groups, use the group structure (may need different payload structure)
    if (recipientMode === "existing" && selectedGroup) {
      return {
        ...basePayload,
        organization_group: selectedGroup.id, // Assuming this is how existing groups are handled
      };
    }

    // Fallback for new groups without categories (shouldn't happen due to validation)
    return basePayload;
  };

  const validateReadyToSchedule = () => {
    const stepOneValid = validateStepOne();
    if (!stepOneValid) {
      setCurrentStep(1);
      return false;
    }

    const recipientsValid = validateRecipientsStep();
    if (!recipientsValid) {
      setCurrentStep(2);
      return false;
    }

    return true;
  };

  const executeSchedule = () => {
    if (scheduleFlow.active) {
      return;
    }

    // Generate UUID for group name when using public link with no recipients
    const finalGroupName =
      formData.createPublicLink && selectedEmployees.length === 0 && !groupName?.trim()
        ? crypto.randomUUID()
        : groupName;

    scheduleSurveyWithGroup({
      formData,
      recipientMode,
      selectedGroupId: selectedGroup?.id,
      selectedEmployees,
      groupName: finalGroupName,
    });

    addToast(
      "Scheduling… We'll create the group and add contacts automatically.",
      { appearance: "info", autoDismiss: true }
    );
  };

  const handleConfirmSchedule = () => {
    setIsConfirmModalOpen(false);
    if (!validateReadyToSchedule()) {
      return;
    }
    executeSchedule();
  };

  const handleCancelSchedule = () => {
    setIsConfirmModalOpen(false);
  };

  // Handle form submission (Step 3)
  const handleSubmit = (e) => {
    e.preventDefault();

    if (scheduleFlow.active) {
      return;
    }

    if (!validateReadyToSchedule()) {
      return;
    }

    setIsConfirmModalOpen(true);
  };

  const breadcrumbCrumbs = (() => {
    const canNavigateBack = !scheduleFlow.active;

    if (currentStep === 1) {
      return [{ label: "Schedule Survey" }];
    }

    const crumbs = [
      {
        label: "Schedule Survey",
        onClick: canNavigateBack ? () => goDirectToStep(1) : undefined,
        disabled: !canNavigateBack,
      },
    ];

    const recipientsCrumb =
      currentStep === 2
        ? { label: "Select Recipients" }
        : {
            label: "Select Recipients",
            onClick: canNavigateBack ? () => goDirectToStep(2) : undefined,
            disabled: !canNavigateBack,
          };

    crumbs.push(recipientsCrumb);

    if (currentStep === 3) {
      crumbs.push({ label: "Confirm & Schedule" });
    }

    return crumbs;
  })();

  return (
    <Container>
      <ScheduleBreadcrumb crumbs={breadcrumbCrumbs} />
      {/* Header */}
      <HeaderSection>
        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>Schedule New Survey</Title>
            <Subtitle>
              {currentStep === 1
                ? "Step 1: Configure your survey settings"
                : currentStep === 2
                ? "Step 2: Select survey recipients"
                : "Step 3: Confirm & schedule survey"}
            </Subtitle>
          </HeaderText>
        </HeaderContent>

        {/* Progress Indicator */}
        <ProgressIndicator>
          <ProgressStep active={currentStep === 1} completed={currentStep > 1}>
            <ProgressStepNumber
              active={currentStep === 1}
              completed={currentStep > 1}
            >
              1
            </ProgressStepNumber>
            <ProgressStepText>Survey Details</ProgressStepText>
          </ProgressStep>
          <ProgressConnector completed={currentStep > 1} />
          <ProgressStep
            active={currentStep === 2}
            completed={currentStep > 2}
          >
            <ProgressStepNumber
              active={currentStep === 2}
              completed={currentStep > 2}
            >
              2
            </ProgressStepNumber>
            <ProgressStepText>Select Recipients</ProgressStepText>
          </ProgressStep>
          <ProgressConnector
            completed={currentStep > 2 || scheduleFlow?.step === "done"}
          />
          <ProgressStep
            active={currentStep === 3}
            completed={scheduleFlow?.step === "done"}
          >
            <ProgressStepNumber
              active={currentStep === 3}
              completed={scheduleFlow?.step === "done"}
            >
              3
            </ProgressStepNumber>
            <ProgressStepText>Confirm &amp; Schedule</ProgressStepText>
          </ProgressStep>
        </ProgressIndicator>
      </HeaderSection>

      {/* Step 1: Survey Details */}
      {currentStep === 1 && (
        <FormContainer>
          <StepFormLayout>
            <StepContentCentered>
              <FormSectionsGrid>
                {/* Left Column */}
                <FormColumn>
                  <PanelSection>
                    <SectionTitle>Survey Configuration</SectionTitle>

                    <FormGroup>
                      <FormLabel>Survey Name *</FormLabel>
                      <FormInput
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                          handleFormChange("name", e.target.value)
                        }
                        placeholder="e.g., Q3 Culture Audit"
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Survey Type</FormLabel>
                      <FormSelect
                        value={formData.surveyType}
                        onChange={(e) =>
                          handleFormChange("surveyType", e.target.value)
                        }
                      >
                        <option value="culture_audit">Culture Audit</option>
                        {/* <option value="360_review">360 Review</option>
                        <option value="edi_survey">EDI Survey</option>
                        <option value="pulse_survey">Pulse Survey</option>
                        <option value="custom">Custom Survey</option> */}
                      </FormSelect>
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Timezone</FormLabel>
                      <FormSelect
                        value={formData.timezone}
                        onChange={(e) =>
                          handleFormChange("timezone", e.target.value)
                        }
                      >
                        {timezones.map((tz) => (
                          <option key={tz.value} value={tz.value}>
                            {tz.label}
                          </option>
                        ))}
                      </FormSelect>
                    </FormGroup>
                  </PanelSection>

                  <PanelSection>
                    <SectionTitle>
                      <LinkIcon />
                      <span>Public Access Options</span>
                    </SectionTitle>

                    <CheckboxGroup>
                      <CheckboxWrapper>
                        <Checkbox
                          type="checkbox"
                          checked={formData.createPublicLink}
                          onChange={(e) =>
                            handleFormChange(
                              "createPublicLink",
                              e.target.checked
                            )
                          }
                        />
                        <CheckboxLabel>
                          Create public link for survey access
                        </CheckboxLabel>
                      </CheckboxWrapper>
                      <CheckboxHint>
                        Allows respondents to access the survey via a shareable
                        link
                      </CheckboxHint>
                    </CheckboxGroup>

                    {formData.createPublicLink && (
                      <CheckboxGroup>
                        <CheckboxWrapper>
                          <Checkbox
                            type="checkbox"
                            checked={formData.useEmployeeIds}
                            onChange={(e) =>
                              handleFormChange(
                                "useEmployeeIds",
                                e.target.checked
                              )
                            }
                          />
                          <CheckboxLabel>
                            Use employee IDs as identifiers
                          </CheckboxLabel>
                        </CheckboxWrapper>
                        <CheckboxHint>
                          Requires respondents to enter their employee ID for
                          tracking
                        </CheckboxHint>
                      </CheckboxGroup>
                    )}
                  </PanelSection>
                </FormColumn>

                {/* Right Column */}
                <FormColumn>
                  <PanelSection>
                    <SectionTitle>
                      <ClockIcon />
                      <span>Survey Schedule</span>
                    </SectionTitle>

                    <FormGroup>
                      <FormLabel>Start Date *</FormLabel>
                      <FormInput
                        type="date"
                        min={startDateMin}
                        value={formData.startDate}
                        onChange={(e) =>
                          handleFormChange("startDate", e.target.value)
                        }
                      />
                      <CheckboxHint>
                        Past dates are allowed. Survey will send immediately if in the past.
                      </CheckboxHint>
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Start Time *</FormLabel>
                      <FormInput
                        type="time"
                        value={formData.startTime}
                        onChange={(e) =>
                          handleFormChange("startTime", e.target.value)
                        }
                      />
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>End Date *</FormLabel>
                      <FormInput
                        type="date"
                        min={endDateMin}
                        value={formData.endDate}
                        onChange={(e) =>
                          handleFormChange("endDate", e.target.value)
                        }
                      />
                      <CheckboxHint>
                        Cannot be before the selected start date.
                      </CheckboxHint>
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>End Time *</FormLabel>
                      <FormInput
                        type="time"
                        value={formData.endTime}
                        onChange={(e) =>
                          handleFormChange("endTime", e.target.value)
                        }
                      />
                    </FormGroup>
                  </PanelSection>
                </FormColumn>
              </FormSectionsGrid>
            </StepContentCentered>

            {/* Step 1 Navigation */}
            <StepNavigationSection>
              <StepNavigationLeft />
              <StepNavigationRight>
                <NextButton
                  type="button"
                  onClick={goToNextStep}
                  disabled={scheduleFlow.active && scheduleFlow.step !== "idle"}
                >
                  {scheduleFlow.active && scheduleFlow.step !== "idle"
                    ? "Working…"
                    : "Continue to Select Recipients"}
                </NextButton>
              </StepNavigationRight>
            </StepNavigationSection>
          </StepFormLayout>
        </FormContainer>
      )}

      {/* Step 2: Employee Selection */}
      {currentStep === 2 && (
        <FormContainer>
          <StepFormLayout>
            <StepContent>
                <PanelSection>
                  <SectionTitle>
                    <UsersIcon />
                    <span>
                      Select Recipients ({selectedEmployees.length} selected)
                    </span>
                  </SectionTitle>
                  <CheckboxHint>
                  {formData.createPublicLink
                    ? "Recipients are optional when using a public link. You can add specific recipients here or proceed without any."
                    : "After scheduling, the Respondents column will reflect the number of contacts in this group."}
                  </CheckboxHint>

                  {/* Group mode toggle */}
                  <TabContainer>
                    <Tab
                      type="button"
                      active={recipientMode === "new"}
                      onClick={() => setRecipientMode("new")}
                    >
                      Create New Group
                    </Tab>
                    <Tab
                      type="button"
                      active={recipientMode === "existing"}
                      onClick={() => setRecipientMode("existing")}
                    >
                      Use Existing Group
                    </Tab>
                  </TabContainer>

                  {recipientMode === "new" && (
                    <PanelSection>
                      <FormGroup>
                        <FormLabel>Group Name *</FormLabel>
                        <FormInput
                          type="text"
                          value={groupName}
                          onChange={(e) => setGroupName(e.target.value)}
                          placeholder="e.g., Q3 Culture Audit – Customer Success"
                        />
                      <CheckboxHint>
                        We’ll create this group and add the selected employees
                        behind the scenes.
                      </CheckboxHint>
                      </FormGroup>
                    </PanelSection>
                  )}
                  {recipientMode === "existing" && (
                    <PanelSection>
                      <FormGroup>
                        <FormLabel>Select Existing Group *</FormLabel>
                        <GroupSearchContainer className="group-search-container">
                          <GroupSearchInputWrapper>
                            <GroupSearchInput
                              type="text"
                              value={groupSearchTerm}
                              onChange={(e) => {
                                setGroupSearchTerm(e.target.value);
                                if (e.target.value.length > 0) {
                                  setIsGroupDropdownOpen(true);
                                }
                              }}
                              onFocus={() => {
                                if (groupSearchTerm.length > 0) {
                                  setIsGroupDropdownOpen(true);
                                }
                              }}
                              placeholder="Search groups by name..."
                            />
                            {!groupSearchTerm && !selectedGroup && (
                              <GroupDropdownButton
                                onClick={() =>
                                  setIsGroupDropdownOpen(!isGroupDropdownOpen)
                                }
                                type="button"
                              >
                                {isGroupDropdownOpen ? "▲" : "▼"}
                              </GroupDropdownButton>
                            )}
                          </GroupSearchInputWrapper>

                          {selectedGroup && (
                            <SelectedGroupDisplay>
                              <GroupDisplayHeader>
                                <GroupDisplayName>
                                  {selectedGroup.name}
                                </GroupDisplayName>
                                <ClearGroupButton
                                  type="button"
                                  aria-label="Remove selected group"
                                  onClick={() => {
                                    setSelectedGroup(null);
                                    setGroupSearchTerm("");
                                    setIsGroupDropdownOpen(true);
                                    dispatch({ type: CLEAR_ORGANIZATION_GROUP });
                                  }}
                                >
                                  ×
                                </ClearGroupButton>
                              </GroupDisplayHeader>
                              <GroupDisplayInfo>
                                {selectedGroup.members_count || 0} contacts •{" "}
                                {selectedGroup.group_type_text || "Survey"}
                              </GroupDisplayInfo>
                            </SelectedGroupDisplay>
                          )}

                          {recipientMode === "existing" &&
                            (isGroupDropdownOpen || groupSearchTerm) && (
                              <GroupSearchResults>
                                {(() => {
                                  // Sort by newest first and filter locally by name (case-insensitive)
                                  const filteredGroups = Array.isArray(organization_groups)
                                    ? [...organization_groups]
                                        .sort((a, b) => (Number(b?.id) || 0) - (Number(a?.id) || 0))
                                        .filter((group) =>
                                          !groupSearchTerm ||
                                          (group?.name || "")
                                            .toLowerCase()
                                            .includes(groupSearchTerm.toLowerCase())
                                        )
                                    : [];

                                  return filteredGroups.length > 0 ? (
                                    <>
                                      {filteredGroups.map((group) => (
                                        <GroupResultItem
                                          key={group.id}
                                          onClick={() => {
                                            setSelectedGroup(group);
                                            setGroupSearchTerm("");
                                            setIsGroupDropdownOpen(false);
                                            if (group?.id) {
                                              dispatch({
                                                type: SG_FETCH_ORGANIZATION_GROUP,
                                                payload: { id: group.id },
                                              });
                                            }
                                          }}
                                          selected={
                                            selectedGroup?.id === group.id
                                          }
                                        >
                                          <GroupResultName>
                                            {group.name}
                                          </GroupResultName>
                                          <GroupResultInfo>
                                            {group.members_count || 0} contacts •{" "}
                                            {group.group_type_text || "Survey"}
                                          </GroupResultInfo>
                                        </GroupResultItem>
                                      ))}
                                      {false && groups_pagination?.next && (
                                        <LoadMoreButton onClick={() => setGroupPage((prev) => prev + 1)}>
                                          Load More Groups
                                        </LoadMoreButton>
                                      )}
                                    </>
                                  ) : (
                                    <NoGroupResults>
                                      {groupSearchTerm
                                        ? "No groups found"
                                        : "No groups available"}
                                    </NoGroupResults>
                                  );
                                })()}
                              </GroupSearchResults>
                            )}
                        <CheckboxHint>
                          {selectedGroup
                            ? `Survey will be sent to ${
                                selectedGroup.members_count || 0
                              } contacts in "${selectedGroup.name}"`
                            : "Search and select a group to schedule the survey to its existing contacts."}
                        </CheckboxHint>
                        </GroupSearchContainer>
                      </FormGroup>
                    </PanelSection>
                  )}

                  {/* Tab Navigation */}
                  {!isExistingGroupView && (
                    <TabContainer>
                      <Tab
                        type="button"
                        active={activeTab === "individual"}
                        onClick={() => {
                          setActiveTab("individual");
                          setSearchTerm("");
                          setSelectedCategories([]);
                        }}
                        disabled={recipientMode === "existing"}
                      >
                        Individual Selection
                      </Tab>
                      <Tab
                        type="button"
                        active={activeTab === "category"}
                        onClick={() => {
                          setActiveTab("category");
                          setSearchTerm("");
                        }}
                        disabled={recipientMode === "existing"}
                      >
                        By Category
                      </Tab>
                    </TabContainer>
                  )}

                  <EmployeeSelectionLayout singleColumn={isExistingGroupView}>
                    {/* Left: Selection Interface */}
                    {!isExistingGroupView && (
                      <SelectionPanel>
                      {/* Individual Selection Tab */}
                      {activeTab === "individual" && (
                        <SelectionContent>
                          <SearchContainer>
                            <SearchInputWrapper>
                              <SearchIconWrapper>
                                <SearchIcon />
                              </SearchIconWrapper>
                              <SearchInput
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                disabled={recipientMode === "existing"}
                                placeholder="Search contacts..."
                              />
                            </SearchInputWrapper>
                            <SearchHint>
                              {searchTerm
                                ? `${filteredEmployees.length} contact${
                                    filteredEmployees.length !== 1 ? "s" : ""
                                  } found`
                                : `${employees.length} total contacts available`}
                            </SearchHint>
                          </SearchContainer>

                          {recipientMode === "existing" ? (
                            <SearchPrompt>
                              <SearchPromptIcon>🔒</SearchPromptIcon>
                              <SearchPromptTitle>
                                Using an existing group
                              </SearchPromptTitle>
                              <SearchPromptText>
                                Employee selection is disabled. Contacts are
                                taken from the group you specified.
                              </SearchPromptText>
                            </SearchPrompt>
                          ) : searchTerm ? (
                            <EmployeesList>
                              {filteredEmployees.map((emp) => (
                                <EmployeeItem
                                  key={emp.id}
                                  selected={selectedEmployees.includes(emp.id)}
                                  onClick={() =>
                                    recipientMode !== "existing" &&
                                    handleEmployeeToggle(emp.id)
                                  }
                                >
                                  <EmployeeCheckbox
                                    type="checkbox"
                                    checked={selectedEmployees.includes(emp.id)}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        recipientMode !== "existing" &&
                                        handleEmployeeToggle(emp.id)
                                      }
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <EmployeeDetails>
                                    <EmployeeName>
                                      {emp.first_name} {emp.last_name}
                                    </EmployeeName>
                                    <EmployeeEmail>{emp.email}</EmployeeEmail>
                                  </EmployeeDetails>
                                </EmployeeItem>
                              ))}
                              {filteredEmployees.length === 0 && (
                                <EmptyState>
                                  <EmptyTitle>No contacts found</EmptyTitle>
                                  <EmptyText>
                                    Try adjusting your search terms
                                  </EmptyText>
                                </EmptyState>
                              )}
                            </EmployeesList>
                          ) : (
                            <SearchPrompt>
                              <SearchPromptIcon>👥</SearchPromptIcon>
                              <SearchPromptTitle>
                                Search for contacts
                              </SearchPromptTitle>
                              <SearchPromptText>
                                Start typing to find contacts by name or email
                              </SearchPromptText>
                            </SearchPrompt>
                          )}
                        </SelectionContent>
                      )}

                      {/* Category Selection Tab */}
                      {activeTab === "category" && (
                        <SelectionContent>
                          <CategoryHeaderRow>
                            <CategoryHeaderTitle>Filter by Tags</CategoryHeaderTitle>
                            <CategoryHeaderSubtitle>
                              Choose a category and value to find matching employees and add them to this survey.
                            </CategoryHeaderSubtitle>
                          </CategoryHeaderRow>
                          {filterCategories.length === 0 ? (
                            <BulkMessageMuted>
                              No tag categories are configured yet. Configure survey tags first, then you can filter by them here.
                            </BulkMessageMuted>
                          ) : (
                            <BulkCategoryBar>
                              <BulkCategorySelectors>
                                <BulkSelect>
                                  <BulkSelectLabel>Category</BulkSelectLabel>
                                  <BulkNativeSelect
                                    value={bulkCategoryId || ""}
                                    onChange={(e) => {
                                      const value = e.target.value || null;
                                      setBulkCategoryId(value ? value : null);
                                      setBulkCategoryValueId(null);
                                    }}
                                  >
                                      <option value="">Select category…</option>
                                      {filterCategories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                          {cat.name}
                                        </option>
                                      ))}
                                    </BulkNativeSelect>
                                  </BulkSelect>
                                  <BulkSelect>
                                    <BulkSelectLabel>Value</BulkSelectLabel>
                                    <Dropdown
                                      selection
                                      search
                                      placeholder="Select value…"
                                      value={bulkCategoryValueId || ""}
                                      onChange={(e, { value }) =>
                                        setBulkCategoryValueId(value || null)
                                      }
                                      disabled={!selectedBulkCategory}
                                      options={sortedCategoryValueOptions}
                                      fluid
                                    />
                                  </BulkSelect>
                                </BulkCategorySelectors>
                                <BulkCategoryActions>
                                  <CategoryActionButton
                                    type="button"
                                    onClick={handleBulkCategoryPreview}
                                    disabled={!bulkCategoryId || !bulkCategoryValueId || bulkLoading}
                                  >
                                    {bulkLoading ? "Checking..." : "Preview matches"}
                                  </CategoryActionButton>
                                  {bulkMatchCount > 0 && (
                                    <CategoryActionButton
                                      type="button"
                                      onClick={handleBulkCategoryConfirm}
                                      disabled={bulkLoading}
                                    >
                                      Add {bulkMatchCount} employee
                                      {bulkMatchCount !== 1 ? "s" : ""}
                                    </CategoryActionButton>
                                  )}
                                </BulkCategoryActions>
                                {bulkMatchCount === 0 && !bulkLoading && (
                                  <BulkMessageMuted>
                                    Choose a category and value, then click "Preview matches" to see how many contacts will be added.
                                  </BulkMessageMuted>
                                )}
                                {bulkMatchCount > 0 && (
                                  <>
                                    <BulkMessage>
                                      Found {bulkMatchCount} matching contact
                                      {bulkMatchCount !== 1 ? "s" : ""} that will be added.
                                    </BulkMessage>
                                    <BulkSampleList>
                                      {bulkResults.slice(0, 10).map((row) => (
                                        <BulkSampleItem
                                          key={row.id || row.employee_id || row.employee}
                                        >
                                          <BulkSampleName>
                                            {row.first_name} {row.last_name}
                                          </BulkSampleName>
                                          {row.email && (
                                            <BulkSampleEmail>{row.email}</BulkSampleEmail>
                                          )}
                                        </BulkSampleItem>
                                      ))}
                                      {/* {bulkMatchCount > 10 && (
                                        <BulkSampleMore>
                                          + {bulkMatchCount - 10} more not shown
                                        </BulkSampleMore>
                                      )} */}
                                    </BulkSampleList>
                                  </>
                                )}
                              </BulkCategoryBar>
                          )}
                        </SelectionContent>
                      )}
                    </SelectionPanel>
                    )}

                    {/* Right: Selected Employees */}
                    <SelectedPanel>
                      <SelectedPanelHeader>
                        <SummaryTitle>
                          {recipientMode === "existing"
                            ? `Group Contacts (${recipientSummaryCount})`
                            : `Selected Recipients (${recipientSummaryCount})`}
                        </SummaryTitle>
                        {recipientMode !== "existing" &&
                          selectedEmployees.length > 0 && (
                          <ClearButton onClick={clearAllEmployees}>
                            Clear All
                          </ClearButton>
                        )}
                      </SelectedPanelHeader>

                      {recipientMode === "existing" ? (
                        <ExistingGroupMembersContainer>
                          {!selectedGroup ? (
                            <EmptySelectedState>
                              <EmptySelectedIcon>🔗</EmptySelectedIcon>
                              <EmptySelectedTitle>No group selected</EmptySelectedTitle>
                              <EmptySelectedText>
                                Choose a group on the left to view its current contacts.
                              </EmptySelectedText>
                            </EmptySelectedState>
                          ) : group_pending || !selectedGroupLoaded ? (
                            <GroupMembersLoading>
                              <GroupMembersLoadingText>
                                Loading group contacts…
                              </GroupMembersLoadingText>
                            </GroupMembersLoading>
                          ) : sortedGroupMembers.length > 0 ? (
                            <>
                              <ExistingGroupInfo>
                                Contacts synced from <strong>{selectedGroup.name}</strong>
                              </ExistingGroupInfo>
                              <GroupMembersTable>
                                <tbody>
                                  {displayedGroupMembers.map((member) => {
                                    const fullName = [member.first_name, member.last_name]
                                      .filter(Boolean)
                                      .join(" ")
                                      .trim();

                                    return (
                                      <GroupMemberRow key={member.id}>
                                        <GroupMemberCell>
                                          <GroupMemberName>
                                            {fullName || member.email || "Unnamed contact"}
                                          </GroupMemberName>
                                          <GroupMemberEmail>{member.email || "No email"}</GroupMemberEmail>
                                        </GroupMemberCell>
                                      </GroupMemberRow>
                                    );
                                  })}
                                  {hasMoreGroupMembers && (
                                    <GroupMemberRow>
                                      <GroupMembersLoadMoreCell
                                        role="button"
                                        tabIndex={0}
                                        onClick={handleLoadMoreMembers}
                                        onKeyDown={(event) => {
                                          if (
                                            event.key === "Enter" ||
                                            event.key === " "
                                          ) {
                                            event.preventDefault();
                                            handleLoadMoreMembers();
                                          }
                                        }}
                                      >
                                        Load more contacts
                                      </GroupMembersLoadMoreCell>
                                    </GroupMemberRow>
                                  )}
                                </tbody>
                              </GroupMembersTable>
                            </>
                          ) : (
                            <EmptySelectedState>
                              <EmptySelectedIcon>👥</EmptySelectedIcon>
                              <EmptySelectedTitle>No contacts found</EmptySelectedTitle>
                              <EmptySelectedText>
                                This group does not currently contain any employees.
                              </EmptySelectedText>
                            </EmptySelectedState>
                          )}
                        </ExistingGroupMembersContainer>
                      ) : selectedEmployees.length > 0 ? (
                        <SelectedEmployeesFullList>
                          {selectedEmployees.map((empId) => {
                            const emp = employees.find((e) => e.id === empId);
                            return emp ? (
                              <SelectedEmployeeFullCard key={empId}>
                                <SelectedEmployeeDetails>
                                  <SelectedEmployeeName>
                                    {emp.first_name} {emp.last_name}
                                  </SelectedEmployeeName>
                                  <SelectedEmployeeEmail>
                                    {emp.email}
                                  </SelectedEmployeeEmail>
                                  {emp.role_name && (
                                    <SelectedEmployeeRole>
                                      {emp.role_name}
                                    </SelectedEmployeeRole>
                                  )}
                                </SelectedEmployeeDetails>
                                <RemoveButton
                                  onClick={() => handleEmployeeToggle(empId)}
                                >
                                  ×
                                </RemoveButton>
                              </SelectedEmployeeFullCard>
                            ) : null;
                          })}
                        </SelectedEmployeesFullList>
                      ) : (
                        <EmptySelectedState>
                          <EmptySelectedIcon>👥</EmptySelectedIcon>
                          <EmptySelectedTitle>
                            No recipients selected
                          </EmptySelectedTitle>
                          <EmptySelectedText>
                            Use the selection options on the left to choose
                            survey recipients.
                          </EmptySelectedText>
                        </EmptySelectedState>
                      )}
                    </SelectedPanel>
                  </EmployeeSelectionLayout>
                </PanelSection>
            </StepContent>

            {/* Flow Status Display */}
            {renderFlowStatus()}

            {/* Step 2 Navigation */}
            <StepNavigationSection>
              <StepNavigationLeft>
                <BackButton
                  type="button"
                  onClick={goToPreviousStep}
                  disabled={scheduleFlow.active}
                >
                  Back to Survey Details
                </BackButton>
              </StepNavigationLeft>
              <StepNavigationRight>
                <ReviewButton
                  type="button"
                  onClick={goToNextStep}
                  disabled={
                    pending ||
                    group_pending ||
                    scheduleFlow.active ||
                    (!formData.createPublicLink &&
                      ((recipientMode === "new" &&
                        selectedEmployees.length === 0) ||
                        (recipientMode === "existing" && !selectedGroup)))
                  }
                >
                  Review &amp; Confirm
                </ReviewButton>
              </StepNavigationRight>
            </StepNavigationSection>
          </StepFormLayout>
        </FormContainer>
      )}

      {/* Step 3: Review & Schedule */}
      {currentStep === 3 && (
        <FormContainer>
          <form onSubmit={handleSubmit}>
            <StepFormLayout>
              <StepContentCentered>
                <ReviewCard>
                  <ReviewCardContent>
                    <ReviewSection>
                      <ReviewSectionTitle>Survey Details</ReviewSectionTitle>
                      <ReviewGrid>
                        <ReviewItem>
                          <ReviewLabel>Survey Name</ReviewLabel>
                          <ReviewValue>
                            {formData.name?.trim() || "Not set"}
                          </ReviewValue>
                        </ReviewItem>
                        <ReviewItem>
                          <ReviewLabel>Survey Type</ReviewLabel>
                          <ReviewValue>{surveyTypeDisplay}</ReviewValue>
                        </ReviewItem>
                        <ReviewItem>
                          <ReviewLabel>Time Zone</ReviewLabel>
                          <ReviewValue>{timezoneDisplayLabel}</ReviewValue>
                        </ReviewItem>
                      </ReviewGrid>
                    </ReviewSection>

                    <ReviewDivider />

                    <ReviewSection>
                      <ReviewSectionTitle>Schedule</ReviewSectionTitle>
                      <ReviewGrid>
                        <ReviewItem>
                          <ReviewLabel>Start Date</ReviewLabel>
                          <ReviewValue>
                            {formatDateDisplay(formData.startDate)}
                          </ReviewValue>
                        </ReviewItem>
                        <ReviewItem>
                          <ReviewLabel>Start Time</ReviewLabel>
                          <ReviewValue>
                            {formatTimeDisplay(formData.startTime)}
                          </ReviewValue>
                        </ReviewItem>
                        <ReviewItem>
                          <ReviewLabel>End Date</ReviewLabel>
                          <ReviewValue>
                            {formatDateDisplay(formData.endDate)}
                          </ReviewValue>
                        </ReviewItem>
                        <ReviewItem>
                          <ReviewLabel>End Time</ReviewLabel>
                          <ReviewValue>
                            {formatTimeDisplay(formData.endTime)}
                          </ReviewValue>
                        </ReviewItem>
                      </ReviewGrid>
                    </ReviewSection>

                    <ReviewDivider />

                    <ReviewSection>
                      <ReviewSectionTitle>Recipients</ReviewSectionTitle>
                      <ReviewGrid>
                        <ReviewItem>
                          <ReviewLabel>Group Name</ReviewLabel>
                          <ReviewValue>
                            {formData.createPublicLink && recipientSummaryCount === 0
                              ? "No specific recipients (Public link only)"
                              : `${groupDisplayName} (${recipientSummaryCount || 0})`}
                          </ReviewValue>
                        </ReviewItem>
                      </ReviewGrid>
                      <ReviewHint>
                        {formData.createPublicLink && recipientSummaryCount === 0
                          ? "Survey will be accessible via public link without specific recipients."
                          : recipientMode === "existing"
                          ? "Survey will use the selected group's current contacts."
                          : "A new group will be created with your selected contacts."}
                      </ReviewHint>
                    </ReviewSection>

                    {publicAccessSummary.length > 0 && (
                      <>
                        <ReviewDivider />
                        <ReviewSection>
                          <ReviewSectionTitle>Public Access</ReviewSectionTitle>
                          <ReviewList>
                            {publicAccessSummary.map((item) => (
                              <ReviewListItem key={item}>{item}</ReviewListItem>
                            ))}
                          </ReviewList>
                        </ReviewSection>
                      </>
                    )}
                  </ReviewCardContent>
                </ReviewCard>
              </StepContentCentered>

              {renderFlowStatus()}

              <StepNavigationSection>
                <StepNavigationLeft>
                  <BackButton
                    type="button"
                    onClick={() => goDirectToStep(2)}
                    disabled={scheduleFlow.active}
                  >
                    Back to Recipients
                  </BackButton>
                </StepNavigationLeft>
                <StepNavigationRight>
                  <SubmitButton
                    type="submit"
                    disabled={
                      pending ||
                      group_pending ||
                      scheduleFlow.active ||
                      (!formData.createPublicLink &&
                        ((recipientMode === "new" &&
                          selectedEmployees.length === 0) ||
                          (recipientMode === "existing" && !selectedGroup)))
                    }
                  >
                    {scheduleFlow.active ? "Scheduling…" : "Confirm & Schedule"}
                  </SubmitButton>
                </StepNavigationRight>
              </StepNavigationSection>

              {isConfirmModalOpen && (
                <ConfirmModalOverlay onClick={handleCancelSchedule}>
                  <ConfirmModalCard onClick={(event) => event.stopPropagation()}>
                    <ConfirmModalHeader>
                      <ConfirmModalTitle>Confirm &amp; Schedule</ConfirmModalTitle>
                      <ConfirmModalBody>
                        Are you ready to schedule <strong>{formData.name?.trim() || "this survey"}</strong> for{" "}
                        <strong>{scheduleWindowSummary}</strong> ({timezoneDisplayLabel})?
                      </ConfirmModalBody>
                      <ConfirmModalHint>
                        {formData.createPublicLink && selectedEmployees.length === 0 && recipientMode === "new"
                          ? "Anyone with the public link will be able to access this survey."
                          : `Recipients in ${groupDisplayName} will be eligible as soon as the survey opens.`}
                      </ConfirmModalHint>
                    </ConfirmModalHeader>
                    <ConfirmModalActions>
                      <ConfirmSecondaryButton type="button" onClick={handleCancelSchedule}>
                        Keep Editing
                      </ConfirmSecondaryButton>
                      <ConfirmPrimaryButton
                        type="button"
                        onClick={handleConfirmSchedule}
                        disabled={scheduleFlow.active}
                      >
                        Schedule Survey
                      </ConfirmPrimaryButton>
                    </ConfirmModalActions>
                  </ConfirmModalCard>
                </ConfirmModalOverlay>
              )}
            </StepFormLayout>
          </form>
        </FormContainer>
      )}
    </Container>
  );
};

const SurveyScheduleWithContext = () => (
  <ScheduleProvider>
    <ScheduleSurvey />
  </ScheduleProvider> 
);

export default SurveyScheduleWithContext;

// Styled Components
const Container = styled.div`
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const FormLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 600px;
`;

const LeftPanel = styled.div`
  padding: 20px;
  border-right: 1px solid #f0f0f0;
`;

const RightPanel = styled.div`
  padding: 20px;
  background: #fafafa;
`;

const PanelSection = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const DateTimeGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const CheckboxGroup = styled.div`
  margin-bottom: 12px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  border-radius: 3px;
`;

const CheckboxLabel = styled.label`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
`;

const CheckboxHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  margin-left: 24px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  background: #f0f0f0;
  padding: 3px;
  border-radius: 6px;
  margin-bottom: 16px;
  width: fit-content;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 4px;
  color: ${props => props.active ? '#414141' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? 'white' : '#e9ecef'};
  }
`;

const SelectionContent = styled.div`
  min-height: 300px;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 6px;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 8px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const SearchHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  padding-left: 12px;
`;

const EmployeesList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const EmployeeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f8f8f8;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.selected && `
    background: #f0f4ff;
    border-color: #476dfa;
  `}
`;

const EmployeeCheckbox = styled.input`
  width: 14px;
  height: 14px;
`;

const EmployeeDetails = styled.div`
  flex: 1;
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
  margin-bottom: 2px;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const SearchPrompt = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SearchPromptIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const SearchPromptTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const SearchPromptText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const CategoryAddSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #e8f5e8;
  border: 1px solid #c8e6c8;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const CategoryAddInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #2e7d2e;
`;

const CategoryAddButton = styled.button`
  padding: 6px 12px;
  background: #2e7d2e;
  border: none;
  border-radius: 4px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #256925;
  }

  &:disabled {
    background: #a5d6a7;
    cursor: not-allowed;
    opacity: 0.8;
  }

  &:disabled:hover {
    background: #a5d6a7;
  }
`;

const CategoryFilters = styled.div`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
`;

const CategoryGroup = styled.div`
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #fafafa;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const CategoryGroupLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCollapseIcon = styled.span`
  font-size: 10px;
  color: #7e7e7e;
  transition: transform 0.2s;
`;

const CategoryName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
`;

const CategoryCount = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const CategoryOptions = styled.div`
  padding: 8px 0;
  background: white;
`;

const CategoryOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 6px 32px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background: ${props => props.disabled ? 'transparent' : '#f8f9fa'};
  }

  ${props => props.selected && !props.disabled && `
    background: #f0f4ff;
  `}
`;

const CategoryOptionCheckbox = styled.input`
  width: 12px;
  height: 12px;
`;

const CategoryOptionText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #414141;
`;

const SelectedSummary = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
`;

const SummaryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SummaryTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
`;

const ClearButton = styled.button`
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #ff4444;
    color: #ff4444;
  }
`;

const SelectedEmployeesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SelectedEmployeeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  background: #f0f4ff;
  border: 1px solid #d0d9ff;
  border-radius: 4px;
`;

const SelectedEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #414141;
`;

const RemoveButton = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  background: #ff4444;
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 10px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #cc3333;
  }
`;

const MoreIndicator = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #7e7e7e;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const EmptyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const SubmitSection = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const SubmitButton = styled.button`
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const ReviewButton = styled(SubmitButton)``;

const CancelButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #ccc;
  }
`;

// Progress Indicator Components
const ProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ProgressStepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s;
  
  ${props => {
    if (props.completed) {
      return `
        background: #2e7d2e;
        color: white;
        border: 2px solid #2e7d2e;
      `;
    } else if (props.active) {
      return `
        background: #476dfa;
        color: white;
        border: 2px solid #476dfa;
      `;
    } else {
      return `
        background: #f0f0f0;
        color: #999;
        border: 2px solid #e0e0e0;
      `;
    }
  }}
`;

const ProgressStepText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  color: #7e7e7e;
  text-align: center;
  white-space: nowrap;
`;

const ProgressConnector = styled.div`
  width: 40px;
  height: 2px;
  background: ${props => props.completed ? '#2e7d2e' : '#e0e0e0'};
  transition: background-color 0.2s;
`;

// Step Layout Components
const StepFormLayout = styled.div`
  min-height: 500px;
  display: flex;
  flex-direction: column;
`;

const StepContent = styled.div`
  flex: 1;
  padding: 24px;
`;

const StepContentCentered = styled.div`
  flex: 1;
  padding: 24px;
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
`;

const FormSectionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StepNavigationSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
  flex-wrap: wrap;
`;

const StepNavigationLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepNavigationRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
`;

const NextButton = styled.button`
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #3d5ce6;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #ccc;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReviewCard = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  width: 100%;
`;

const ReviewCardContent = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ReviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ReviewSectionTitle = styled.h4`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
  margin: 0;
`;

const ReviewGrid = styled.div`
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  width: 100%;
`;

const ReviewItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReviewLabel = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #7e7e7e;
`;

const ReviewValue = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  word-break: break-word;
`;

const ReviewHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  line-height: 1.4;
  margin-top: 4px;
`;

const ReviewDivider = styled.div`
  height: 1px;
  background: #f0f0f0;
  width: 100%;
`;

const ReviewList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ReviewListItem = styled.li`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
`;

const ConfirmModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(4px);
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ConfirmModalCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 22px 45px rgba(15, 23, 42, 0.28);
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
`;

const ConfirmModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ConfirmModalTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #1f2937;
  margin: 0;
`;

const ConfirmModalBody = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 14px;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;

  strong {
    font-weight: 600;
    color: #1f2937;
  }
`;

const ConfirmModalHint = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;

  strong {
    font-weight: 600;
    color: #1f2937;
  }
`;

const ConfirmModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const ConfirmSecondaryButton = styled.button`
  padding: 12px 24px;
  background: #f1f5f9;
  border: 1px solid #d0d7e2;
  border-radius: 6px;
  color: #475569;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
  }
`;

const ConfirmPrimaryButton = styled.button`
  padding: 12px 24px;
  background: #476dfa;
  border: none;
  border-radius: 6px;
  color: white;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
    transform: translateY(-1px);
  }

  &:disabled {
    background: #a2b3ff;
    cursor: not-allowed;
  }
`;

// Employee Selection Layout
const EmployeeSelectionLayout = styled.div`
  display: grid;
  grid-template-columns: ${(props) => (props.singleColumn ? "1fr" : "1fr 1fr")};
  gap: 24px;
  margin-top: 16px;
  min-height: 500px;
`;

const SelectionPanel = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const SelectedPanel = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SelectedPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const ExistingGroupMembersContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 12px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const ExistingGroupInfo = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;

  strong {
    color: #476dfa;
  }
`;

const GroupMembersTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  font-family: "Poppins";
`;

const GroupMemberRow = styled.tr``;

const GroupMemberCell = styled.td`
  background: #ffffff;
  border: 1px solid #dce3ff;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
`;

const GroupMemberName = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #2b2b2b;
`;

const GroupMemberEmail = styled.div`
  font-weight: 400;
  font-size: 12px;
  color: #69707f;
  margin-top: 4px;
  word-break: break-word;
`;

const GroupMembersLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #4a64f5;
`;

const GroupMembersLoadingText = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
`;

const GroupMembersLoadMoreCell = styled(GroupMemberCell)`
  text-align: center;
  cursor: pointer;
  font-weight: 600;
  color: #4a64f5;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;

  &:hover {
    background: rgba(74, 100, 245, 0.12);
    color: #2f4be1;
    box-shadow: 0 2px 6px rgba(74, 100, 245, 0.18);
  }

  &:focus {
    outline: 2px solid #4a64f5;
    outline-offset: 2px;
  }
`;

const SelectedEmployeesFullList = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  max-height: 400px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SelectedEmployeeFullCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectedEmployeeDetails = styled.div`
  flex: 1;
`;

const SelectedEmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  margin-bottom: 2px;
`;

const SelectedEmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #999;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 4px;
`;

const EmptySelectedState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptySelectedIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptySelectedTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const EmptySelectedText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  max-width: 250px;
  line-height: 1.4;
`;

// Group Search Styles
const GroupSearchContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

const GroupSearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const GroupSearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 13px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const GroupDropdownButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #7e7e7e;
  cursor: pointer;
  padding: 4px;
  font-size: 12px;
  transition: color 0.2s;

  &:hover {
    color: #476dfa;
  }
`;

const SelectedGroupDisplay = styled.div`
  margin-top: 8px;
  padding: 12px;
  background: #f0f4ff;
  border: 1px solid #d0d9ff;
  border-radius: 6px;
`;

const GroupDisplayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const GroupDisplayName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const GroupDisplayInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const ClearGroupButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: #4a64f5;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(74, 100, 245, 0.1);
    color: #2f4be1;
  }

  &:focus {
    outline: 2px solid #4a64f5;
    outline-offset: 2px;
  }
`;

const GroupSearchResults = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
  margin-top: 4px;
`;

const GroupResultItem = styled.div`
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.selected && `
    background: #f0f4ff;
    border-color: #476dfa;
  `}
`;

const GroupResultName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 4px;
`;

const GroupResultInfo = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const LoadMoreButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: #f8f9fa;
  border: none;
  border-top: 1px solid #f0f0f0;
  color: #476dfa;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #e9ecef;
  }
`;

const NoGroupResults = styled.div`
  padding: 16px;
  text-align: center;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

// Category Selection Styles
const MultiSelectWrapper = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  background: #fafafa;
`;

const CategorySelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CategoryCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CategoryCheckbox = styled.input`
  width: 16px;
  height: 16px;
  border-radius: 3px;
`;

const CategoryCheckboxLabel = styled.label`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
  cursor: pointer;
`;

const EmptyStateText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  text-align: center;
  padding: 16px;
`;

// Category Info Banner Styles
const CategoryInfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f0f4ff;
  border: 1px solid #d0d9ff;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const CategoryInfoIcon = styled.div`
  font-size: 16px;
`;

const CategoryInfoText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #414141;
  line-height: 1.4;
`;

// Flow Status Components
const FlowStatusSection = styled.div`
  background: #f0f8ff;
  border: 1px solid #b3d9ff;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
`;

const FlowStatusHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #2563eb;
`;

const FlowSteps = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FlowStep = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: ${props => props.completed ? '#059669' : '#6b7280'};
`;

const FlowStepIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => 
    props.active ? '#2563eb' : 
    props.completed ? '#059669' : '#d1d5db'
  };
  border: 2px solid ${props => 
    props.active ? '#2563eb' : 
    props.completed ? '#059669' : '#d1d5db'
  };
  position: relative;
  
  ${props => props.active && `
    animation: pulse 2s infinite;
    
    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7); }
      70% { box-shadow: 0 0 0 4px rgba(37, 99, 235, 0); }
      100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); }
    }
  `}
`;

const FlowStepConnector = styled.div`
  width: 20px;
  height: 1px;
  background: #d1d5db;
  margin: 0 4px;
`;

// Bulk Category Dropdown Styles
const CategoryHeaderRow = styled.div`
  margin-bottom: 12px;
`;

const CategoryHeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
`;

const CategoryHeaderSubtitle = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
`;

const BulkCategoryBar = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #f9fafb;
`;

const BulkCategorySelectors = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

const BulkSelect = styled.div`
  flex: 1;
`;

const BulkSelectLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
`;

const BulkNativeSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  background: #ffffff;
`;

const BulkCategoryActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const CategoryActionButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: #2d70e2;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const BulkMessage = styled.div`
  font-size: 12px;
  color: #374151;
  margin-top: 4px;
`;

const BulkMessageMuted = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
`;

const BulkSampleList = styled.div`
  margin-top: 8px;
  border-top: 1px solid #e5e7eb;
  padding-top: 8px;
`;

const BulkSampleItem = styled.div`
  padding: 4px 0;
`;

const BulkSampleName = styled.div`
  font-size: 13px;
  font-weight: 500;
`;

const BulkSampleEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const BulkSampleMore = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;
